import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useAuth } from "@/lib/context/auth-context";
import { 
  BookOpen, Clock, GraduationCap, Calendar, Award, AlertCircle, 
  Users, FileCheck, School, Target, Trophy, CalendarDays, History,
  Settings, User, MapPin, Activity, CheckCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";
import { CertificateCard } from "@/components/certificates/CertificateCard";
import { Badge } from "@/components/ui/badge";
import { ProfileCompletionPopup } from "@/components/auth/ProfileCompletionPopup";

interface CourseStats {
  enrolled: number;
  completed: number;
  inProgress: number;
  waitlisted: number;
}

interface StaffStats {
  totalUsers: number;
  totalCourses: number;
  activeCourses: number;
  pendingCertificates: number;
  averageCEUHours: number;
}

interface UpcomingEvent {
  id: string;
  title: string;
  start_date: string;
  venue_details: string;
}

interface CourseRegistrationWithCertificate {
  certificates: {
    id: string;
    issue_date: string;
  }[];
  courses: {
    title: string;
    hours: number;
  } | null;
}

interface Achievements {
  totalHours: number;
  completionStreak: number;
  categoriesExplored: string[];
  lastCompletionDate?: string | null;
}

const Dashboard = () => {
  const { profile, user } = useAuth();
  const { toast } = useToast();
  const isStaff = profile?.role === 'staff' || profile?.role === 'admin';
  
  // Get time of day for personalized greeting
  const getTimeOfDay = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Morning";
    if (hour < 18) return "Afternoon";
    return "Evening";
  };

  const { data: staffStats = { totalUsers: 0, totalCourses: 0, activeCourses: 0, pendingCertificates: 0, averageCEUHours: 0 } } = useQuery({
    queryKey: ['staffStats', user?.id],
    queryFn: async () => {
      if (!isStaff) return { totalUsers: 0, totalCourses: 0, activeCourses: 0, pendingCertificates: 0, averageCEUHours: 0 };

      const { data: users, error: usersError } = await supabase
        .from('profiles')
        .select('id');

      const { data: courses, error: coursesError } = await supabase
        .from('courses')
        .select('id, status, start_date');

      const { data: certificates, error: certificatesError } = await supabase
        .from('certificates')
        .select('id');
      
      const { data: certificateHours, error: hoursError } = await supabase
        .from('certificates')
        .select(`
          courses:course_id (
            hours
          )
        `);

      if (usersError || coursesError || certificatesError || hoursError) {
        toast({
          title: "Error",
          description: "Failed to fetch staff statistics",
          variant: "destructive",
        });
        return { totalUsers: 0, totalCourses: 0, activeCourses: 0, pendingCertificates: 0, averageCEUHours: 0 };
      }

      let averageCEUHours = 0;
      if (certificateHours && certificateHours.length > 0) {
        const totalHours = certificateHours.reduce((sum, cert) => {
          return sum + (cert.courses?.hours || 0);
        }, 0);
        averageCEUHours = parseFloat((totalHours / certificateHours.length).toFixed(1));
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0); // Set to beginning of the day

      return {
        totalUsers: users?.length || 0,
        totalCourses: courses?.length || 0,
        activeCourses: courses?.filter(c => 
          c.status === 'published' && 
          c.start_date && 
          new Date(c.start_date) >= today
        ).length || 0,
        pendingCertificates: certificates?.length || 0,
        averageCEUHours
      };
    },
    enabled: isStaff,
  });

  const { data: courseStats = { enrolled: 0, completed: 0, inProgress: 0, waitlisted: 0 } } = useQuery({
    queryKey: ['courseStats', user?.id],
    queryFn: async () => {
      if (!user?.id) return { enrolled: 0, completed: 0, inProgress: 0, waitlisted: 0 };
      
      const { data: registrations, error: regError } = await supabase
        .from('course_registrations')
        .select('status')
        .eq('user_id', user.id)
        .eq('status', 'registered')
        .is('attendance_date', null);

      if (regError) {
        console.error('Registration error:', regError);
        toast({
          title: "Error",
          description: "Failed to fetch course statistics",
          variant: "destructive",
        });
        return { enrolled: 0, completed: 0, inProgress: 0, waitlisted: 0 };
      }

      const { data: certificates, error: certError } = await supabase
        .from('certificates')
        .select('id')
        .eq('user_id', user.id);

      if (certError) {
        console.error('Certificates error:', certError);
        toast({
          title: "Error",
          description: "Failed to fetch certificates",
          variant: "destructive",
        });
      }

      return {
        enrolled: registrations?.filter(r => r.status === 'registered').length || 0,
        completed: certificates?.length || 0,
        inProgress: registrations?.filter(r => r.status === 'in_progress').length || 0,
        waitlisted: registrations?.filter(r => r.status === 'waitlisted').length || 0,
      };
    },
  });

  const { data: achievements = { totalHours: 0, completionStreak: 0, categoriesExplored: [], lastCompletionDate: null }, error: achievementsError } = useQuery({
    queryKey: ['achievements', user?.id],
    queryFn: async () => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }
      
      try {
        const { data: completedCourses, error: completedError } = await supabase
          .from('certificates')
          .select(`
            issue_date,
            courses:course_id (
              hours,
              category
            )
          `)
          .eq('user_id', user.id)
          .order('issue_date', { ascending: false });

        if (completedError) {
          throw new Error(`Failed to fetch completed courses: ${completedError.message}`);
        }

        if (!completedCourses) {
          return {
            totalHours: 0,
            completionStreak: 0,
            categoriesExplored: [],
            lastCompletionDate: null
          };
        }

        const validCompletions = completedCourses.filter(cert => cert.courses !== null);

        const totalHours = validCompletions.reduce((sum, cert) => 
          sum + (cert.courses?.hours || 0), 0
        );

        const categories = [...new Set(validCompletions
          .map(cert => cert.courses?.category)
          .filter(Boolean)
        )];

        let streak = 0;
        let lastDate: Date | null = null;

        if (validCompletions.length > 0) {
          const sortedCompletions = validCompletions
            .filter(cert => cert.issue_date)
            .sort((a, b) => 
              new Date(b.issue_date).getTime() - new Date(a.issue_date).getTime()
            );

          if (sortedCompletions.length > 0) {
            lastDate = new Date(sortedCompletions[0].issue_date);
            streak = 1;

            for (let i = 1; i < sortedCompletions.length; i++) {
              const currentDate = new Date(sortedCompletions[i].issue_date);
              if (!isNaN(currentDate.getTime())) {
                const daysDifference = Math.floor(
                  (lastDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24)
                );

                if (daysDifference <= 30) {
                  streak++;
                  lastDate = currentDate;
                } else {
                  break;
                }
              }
            }
          }
        }

        return {
          totalHours,
          completionStreak: streak,
          categoriesExplored: categories,
          lastCompletionDate: lastDate?.toISOString()
        };
      } catch (error) {
        console.error('Error calculating achievements:', error);
        toast({
          title: "Error Loading Achievements",
          description: "Failed to load your learning achievements. Please try again later.",
          variant: "destructive",
        });
        throw error;
      }
    },
    retry: 2,
    staleTime: 5 * 60 * 1000,
  });

  const { data: upcomingEvents = [], isLoading: isLoadingEvents, error: eventsError } = useQuery({
    queryKey: ['upcomingEvents', user?.id],
    queryFn: async () => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      try {
        const { data: registrations, error: regError } = await supabase
          .from('course_registrations')
          .select('course_id')
          .eq('user_id', user.id)
          .eq('status', 'registered')
          .is('attendance_date', null);

        if (regError) {
          throw new Error(`Failed to fetch registrations: ${regError.message}`);
        }

        const courseIds = registrations?.map(reg => reg.course_id) || [];

        const { data: events, error: eventsError } = await supabase
          .from('courses')
          .select(`
            id,
            title,
            start_date,
            start_time,
            end_time,
            venue_details,
            location,
            specific_location
          `)
          .eq('event_type', 'live')
          .eq('status', 'published')
          .in('id', courseIds)
          .gt('start_date', new Date().toISOString())
          .order('start_date', { ascending: true })
          .limit(5);

        if (eventsError) {
          throw new Error(`Failed to fetch events: ${eventsError.message}`);
        }

        return (events || []).map(event => ({
          id: event.id,
          title: event.title,
          start_date: event.start_date,
          venue_details: event.venue_details || event.specific_location || event.location || 'Location TBD',
          start_time: event.start_time,
          end_time: event.end_time
        }));

      } catch (error) {
        console.error('Error fetching upcoming events:', error);
        toast({
          title: "Error Loading Events",
          description: "Failed to load your upcoming events. Please try again later.",
          variant: "destructive",
        });
        throw error;
      }
    },
    retry: 2,
    staleTime: 5 * 60 * 1000,
  });

  const { data: allUpcomingEvents = [], isLoading: isLoadingAllEvents, error: allEventsError } = useQuery({
    queryKey: ['allUpcomingEvents'],
    queryFn: async () => {
      try {
        const now = new Date();
        const thirtyDaysLater = new Date(now);
        thirtyDaysLater.setDate(now.getDate() + 30);
        
        const { data: events, error: eventsError } = await supabase
          .from('courses')
          .select(`
            id,
            title,
            start_date,
            venue_details,
            location,
            specific_location,
            start_time,
            end_time,
            hours
          `)
          .eq('status', 'published')
          .gte('start_date', now.toISOString())
          .lte('start_date', thirtyDaysLater.toISOString())
          .order('start_date', { ascending: true })
          .limit(2);

        if (eventsError) {
          throw new Error(`Failed to fetch all upcoming events: ${eventsError.message}`);
        }

        return (events || []).map(event => ({
          id: event.id,
          title: event.title,
          start_date: event.start_date,
          hours: event.hours,
          venue_details: event.venue_details || event.specific_location || event.location || 'Location TBD',
          start_time: event.start_time,
          end_time: event.end_time
        }));
      } catch (error) {
        console.error('Error fetching all upcoming events:', error);
        toast({
          title: "Error Loading Events",
          description: "Failed to load upcoming events. Please try again later.",
          variant: "destructive",
        });
        throw error;
      }
    },
    enabled: isStaff,
    retry: 2,
    staleTime: 5 * 60 * 1000,
  });

  const { data: recentCertificates = [], isLoading: isLoadingCertificates, error: certificatesError } = useQuery({
    queryKey: ['recentCertificates', user?.id],
    queryFn: async () => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }
      
      try {
        const { data, error } = await supabase
          .from('certificates')
          .select(`
            id,
            issue_date,
            course_id,
            courses:course_id (
              title,
              hours
            )
          `)
          .eq('user_id', user.id)
          .order('issue_date', { ascending: false })
          .limit(3);

        if (error) {
          throw new Error(`Failed to fetch certificates: ${error.message}`);
        }

        return data?.map(cert => ({
          id: cert.id,
          course_title: cert.courses?.title,
          completion_date: cert.issue_date,
          hours: cert.courses?.hours
        })) || [];
      } catch (error) {
        console.error('Error fetching recent certificates:', error);
        toast({
          title: "Error",
          description: "Failed to fetch certificates. Please try again later.",
          variant: "destructive",
        });
        throw error;
      }
    },
    enabled: !!user?.id,
    retry: 2,
    staleTime: 5 * 60 * 1000,
  });

  const { data: upcomingCoursesPeek = [], isLoading: isLoadingUpcoming } = useQuery({
    queryKey: ['upcomingCoursesPeek'],
    queryFn: async () => {
      try {
        const { data: courses, error } = await supabase
          .from('courses')
          .select(`
            id,
            title,
            start_date,
            start_time,
            end_time,
            venue_details,
            location,
            specific_location,
            hours,
            event_type,
            description
          `)
          .eq('status', 'published')
          .gte('start_date', new Date().toISOString())
          .order('start_date', { ascending: true })
          .limit(2);

        if (error) {
          throw error;
        }

        return courses;
      } catch (error) {
        console.error('Error fetching upcoming courses:', error);
        toast({
          title: "Error",
          description: "Failed to load upcoming courses preview",
          variant: "destructive",
        });
        return [];
      }
    },
    staleTime: 5 * 60 * 1000,
  });

  const AchievementsContent = () => {
    if (achievementsError) {
      return (
        <div className="flex flex-col items-center justify-center space-y-2 py-4">
          <AlertCircle className="h-8 w-8 text-destructive" />
          <p className="text-sm text-muted-foreground text-center">
            Unable to load achievements
          </p>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => {
              window.location.reload();
            }}
          >
            Try Again
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Total Hours</span>
          <span className="font-bold">{achievements.totalHours} hours</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Course Streak</span>
          <span className="font-bold">{achievements.completionStreak} courses</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Categories Explored</span>
          <span className="font-bold">{achievements.categoriesExplored.length}</span>
        </div>
        {achievements.lastCompletionDate && (
          <div className="text-xs text-muted-foreground text-center mt-2">
            Last completion: {new Date(achievements.lastCompletionDate).toLocaleDateString()}
          </div>
        )}
      </div>
    );
  };

  const LearningScheduleContent = () => {
    if (isLoadingEvents) {
      return (
        <div className="space-y-4 animate-pulse">
          <div className="h-12 bg-secondary/20 rounded"></div>
          <div className="h-12 bg-secondary/20 rounded"></div>
        </div>
      );
    }

    if (eventsError) {
      return (
        <div className="flex flex-col items-center justify-center space-y-2 py-4">
          <AlertCircle className="h-8 w-8 text-destructive" />
          <p className="text-sm text-muted-foreground text-center">
            Unable to load schedule
          </p>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => {
              window.location.reload();
            }}
          >
            Try Again
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="space-y-2">
          {upcomingEvents.length > 0 ? (
            upcomingEvents.map((event) => (
              <div key={event.id} className="flex items-start space-x-2 text-sm">
                <Calendar className="h-4 w-4 text-primary mt-0.5" />
                <div>
                  <p className="font-medium">{event.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(event.start_date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>
                  {event.venue_details && (
                    <p className="text-sm text-muted-foreground">{event.venue_details}</p>
                  )}
                </div>
              </div>
            ))
          ) : (
            <p className="text-sm text-muted-foreground text-center py-2">No upcoming events</p>
          )}
        </div>
        <Button variant="outline" size="sm" className="w-full" asChild>
          <Link to="/courses">Browse More Courses</Link>
        </Button>
      </div>
    );
  };

  const RecentActivityContent = () => {
    if (isLoadingCertificates) {
      return (
        <div className="space-y-4 animate-pulse">
          <div className="h-12 bg-secondary/20 rounded"></div>
          <div className="h-12 bg-secondary/20 rounded"></div>
        </div>
      );
    }

    if (certificatesError) {
      return (
        <div className="flex flex-col items-center justify-center space-y-2 py-4">
          <AlertCircle className="h-8 w-8 text-destructive" />
          <p className="text-sm text-muted-foreground text-center">
            Unable to load recent activity
          </p>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => {
              window.location.reload();
            }}
          >
            Try Again
          </Button>
        </div>
      );
    }

    if (recentCertificates.length > 0) {
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            {recentCertificates.map((cert) => (
              <div key={cert.id} className="flex items-start space-x-2 text-sm">
                <Award className="h-4 w-4 text-primary mt-0.5" />
                <div>
                  <p className="font-medium">{cert.course_title}</p>
                  <p className="text-xs text-muted-foreground">
                    Completed {new Date(cert.completion_date).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <Button variant="outline" size="sm" className="w-full" asChild>
            <Link to="/certificates">View All Certificates</Link>
          </Button>
        </div>
      );
    }

    return (
      <div className="text-center py-8">
        <Award className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-sm text-muted-foreground mb-4">No recent activity</p>
        <Button variant="outline" size="sm" asChild>
          <Link to="/courses">Start Learning</Link>
        </Button>
      </div>
    );
  };

  const UpcomingCoursesPreview = () => (
    <Card className="hover-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarDays className="h-5 w-5 text-primary" />
          Upcoming Courses Preview
        </CardTitle>
        <CardDescription className="font-serif">
          Next available professional development opportunities
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoadingUpcoming ? (
        <div className="space-y-4">
            <div className="h-24 bg-muted animate-pulse rounded-lg" />
            <div className="h-24 bg-muted animate-pulse rounded-lg" />
            </div>
        ) : upcomingCoursesPeek.length > 0 ? (
          <div className="space-y-6">
            {upcomingCoursesPeek.map((course) => (
              <Link 
                key={course.id} 
                to={`/courses/${course.id}`}
                className="block"
              >
                <div className="p-2 rounded-lg bg-accent/5 hover:bg-accent/10 transition-all duration-200 space-y-2 border border-accent/10 hover:border-accent/20">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-start justify-between gap-3">
                      <h4 className="font-medium text-sm text-primary">{course.title}</h4>
                      <Badge variant="accent" className="shrink-0 text-xs px-2 py-0.5">
                        {course.hours} CEUs
                      </Badge>
            </div>
                    {course.description && (
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {course.description}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 text-xs">
                    <div className="flex items-center gap-1 text-accent">
                      <CalendarDays className="h-3 w-3" />
                      <span>
                        {new Date(course.start_date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                    })}
                      </span>
                    </div>
                    {course.venue_details && (
                      <div className="flex items-center gap-1 text-accent">
                        <MapPin className="h-3 w-3" />
                        <span>{course.venue_details}</span>
                      </div>
                    )}
                    {course.event_type && (
                      <div className="flex items-center gap-1 mt-0.5">
                        <Badge variant="outline" className="text-[10px] px-1 py-0.5">
                          {course.event_type === 'live' ? 'Live Event' : 'Self-Paced'}
                        </Badge>
                      </div>
                  )}
                </div>
              </div>
              </Link>
            ))}
            <div className="text-center pt-2">
              <Button variant="accent" size="sm" asChild className="w-full">
                <Link to="/courses">View All Courses</Link>
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <CalendarDays className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-sm text-muted-foreground mb-4">No upcoming courses scheduled</p>
            <Button variant="accent" size="sm" asChild>
              <Link to="/courses">Browse All Courses</Link>
              </Button>
            </div>
          )}
      </CardContent>
    </Card>
  );

  const QuickLinksCard = () => (
    <Card className="hover-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {isStaff ? (
            <>
              <Users className="h-5 w-5" />
              Quick Actions
            </>
          ) : (
            <>
              <BookOpen className="h-5 w-5" />
              Quick Links
            </>
          )}
        </CardTitle>
        <CardDescription>
          {isStaff ? "Manage courses and users" : "Quick access to important features"}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isStaff ? (
          <div className="space-y-3">
            <Button asChild variant="accent" className="w-full">
              <Link to="/admin/courses/new">
                <School className="h-4 w-4 mr-2" />
                Create New Course
              </Link>
            </Button>
            <Button asChild variant="accent" className="w-full">
              <Link to="/admin/courses">
                <BookOpen className="h-4 w-4 mr-2" />
                Manage Courses
              </Link>
            </Button>
            <Button asChild variant="accent" className="w-full">
              <Link to="/admin/users">
                <Users className="h-4 w-4 mr-2" />
                Manage Users
              </Link>
            </Button>
            <Button asChild variant="accent" className="w-full">
              <Link to="/admin/certificates">
                <Award className="h-4 w-4 mr-2" />
                Manage Certificates
              </Link>
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <Button asChild variant="accent" className="w-full">
              <Link to="/courses">
                <BookOpen className="h-4 w-4 mr-2" />
                Browse Courses
              </Link>
            </Button>
            <Button asChild variant="accent" className="w-full">
              <Link to="/certificates">
                <Award className="h-4 w-4 mr-2" />
                View Certificates
              </Link>
            </Button>
            <Button asChild variant="accent" className="w-full">
              <Link to="/profile">
                <User className="h-4 w-4 mr-2" />
                My Profile
              </Link>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="w-full space-y-6 animate-fade-in px-4 md:px-6 overflow-y-auto h-[calc(100vh-9rem)]">
      {/* Critical Alert Placeholder */}
      {/* Example: <Alert variant="destructive" className="mb-4">Course starting soon!</Alert> */}
      {/* You can add logic to show this only when needed */}

      <ProfileCompletionPopup open={false} onOpenChange={() => {}} incompleteFields={[]} />
      
      <div className="w-full bg-gradient-to-r from-primary/10 to-accent/5 rounded-xl p-6 shadow-sm border border-primary/10">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4 mb-2">
            {/* Profile Avatar removed */}
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-primary mb-1">
                Good {getTimeOfDay()}, {profile?.first_name || 'User'}!
              </h1>
              <p className="text-muted-foreground font-serif text-lg">
                {isStaff 
                  ? "Welcome to your administrative dashboard. Manage courses, track registrations, and analyze user progress."
                  : "Track your CEU progress and discover new professional development opportunities."}
              </p>
              {/* Profile Completion Progress Bar */}
              {profile?.completion < 100 && (
                <div className="mt-2 w-full max-w-xs">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                    <span>Profile {profile.completion || 0}% complete</span>
                    <Button variant="link" size="sm" className="p-0 h-4 text-accent" onClick={() => {}}>
                      Complete your profile
                    </Button>
          </div>
                  <div className="w-full h-2 bg-accent-subtle rounded-full overflow-hidden">
                    <div className="h-2 bg-accent rounded-full" style={{ width: `${profile.completion || 0}%` }} />
                  </div>
                    </div>
                  )}
                </div>
          </div>
          <div className="flex-shrink-0">
            <Button asChild variant="accent" size="lg" className="shadow-sm">
              <Link to={isStaff ? "/admin/courses/new" : "/courses"}>
                {isStaff ? (
                  <>
                    <School className="h-5 w-5 mr-2" />
                    Create New Course
                  </>
                ) : (
                  <>
                    <BookOpen className="h-5 w-5 mr-2" />
                    Browse Courses
                  </>
                )}
              </Link>
              </Button>
            </div>
          </div>
          </div>

      {/* Section Divider */}
      <div className="my-6 border-t border-accent-light/30" />

      <div className="w-full">
        {isStaff ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="hover-card hover:border-[#0f8f8c]/50 transition-colors">
              <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                <CardTitle className="text-sm font-medium">Average CEU Hours</CardTitle>
                <Award className="h-4 w-4 text-[#0f8f8c]" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-[#004161]">{staffStats.averageCEUHours}</div>
                <p className="text-xs text-muted-foreground">
                  Hours per certificate
                </p>
              </CardContent>
            </Card>
            <Card className="hover-card hover:border-[#0f8f8c]/50 transition-colors">
              <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                <CardTitle className="text-sm font-medium">Total Courses</CardTitle>
                <School className="h-4 w-4 text-[#0f8f8c]" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-[#004161]">{staffStats.totalCourses}</div>
                <p className="text-xs text-muted-foreground">
                  Available courses
                </p>
              </CardContent>
            </Card>
            <Card className="hover-card hover:border-[#0f8f8c]/50 transition-colors">
              <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                <CardTitle className="text-sm font-medium">Active Courses</CardTitle>
                <BookOpen className="h-4 w-4 text-[#0f8f8c]" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-[#004161]">{staffStats.activeCourses}</div>
                <p className="text-xs text-muted-foreground">
                  Currently published
                </p>
                <Button asChild variant="link" className="w-full mt-2 p-0 hover:bg-[#0f8f8c]/10 hover:text-[#004161] hover:border-[#0f8f8c]">
                  <Link to="/admin/courses/new">Create New Course</Link>
                </Button>
              </CardContent>
            </Card>
            <Card className="hover-card hover:border-[#0f8f8c]/50 transition-colors">
              <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                <CardTitle className="text-sm font-medium">Issued Certificates</CardTitle>
                <FileCheck className="h-4 w-4 text-[#0f8f8c]" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-[#004161]">{staffStats.pendingCertificates}</div>
                <p className="text-xs text-muted-foreground">
                  Issued
                </p>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="hover-card">
              <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                <CardTitle className="text-sm font-medium">Enrolled Courses</CardTitle>
                <BookOpen className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{courseStats.enrolled}</div>
                <p className="text-xs text-muted-foreground">
                  Active enrollments
                </p>
              </CardContent>
            </Card>
            <Card className="hover-card">
              <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                <CardTitle className="text-sm font-medium">In Progress</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{courseStats.inProgress}</div>
                <p className="text-xs text-muted-foreground">
                  Courses in progress
                </p>
                <Button variant="link" className="w-full mt-2 p-0 hover:bg-[#0f8f8c]/10 hover:text-[#004161] hover:border-[#0f8f8c]">
                  <Link to="/check-in">
                    <Clock className="h-4 w-4 mr-2" />
                    Check In
                  </Link>
                </Button>
              </CardContent>
            </Card>
            <Card className="hover-card">
              <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                <CardTitle className="text-sm font-medium">Completed CEUs</CardTitle>
                <GraduationCap className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{courseStats.completed}</div>
                <p className="text-xs text-muted-foreground">
                  Earned certificates
                </p>
              </CardContent>
            </Card>
            <Card className="hover-card">
              <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                <CardTitle className="text-sm font-medium">Waitlisted</CardTitle>
                <History className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{courseStats.waitlisted}</div>
                <p className="text-xs text-muted-foreground">
                  On waitlist
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      
      <div className="w-full">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <UpcomingCoursesPreview />
          <QuickLinksCard />
        </div>
      </div>

      {/* Section Divider */}
      <div className="my-6 border-t border-accent-light/30" />

      <div className="w-full">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {isStaff ? (
            <>
              <Card className="md:col-span-3">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Upcoming Events
                  </CardTitle>
                  <CardDescription className="font-serif">
                    Scheduled events for the next 30 days
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {isLoadingAllEvents ? (
                    <div className="space-y-3">
                      <div className="h-14 bg-secondary/20 rounded-lg animate-pulse" />
                      <div className="h-14 bg-secondary/20 rounded-lg animate-pulse" />
                    </div>
                  ) : allEventsError ? (
                    <div className="flex flex-col items-center justify-center py-6 space-y-3">
                      <AlertCircle className="h-8 w-8 text-destructive" />
                      <p className="text-sm text-muted-foreground text-center">
                        Failed to load upcoming events
                      </p>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          window.location.reload();
                        }}
                      >
                        Try Again
                      </Button>
                    </div>
                  ) : allUpcomingEvents.length > 0 ? (
                    <div className="space-y-3">
                      {allUpcomingEvents.map((event) => (
                        <div 
                          key={event.id} 
                          className="flex flex-col space-y-1 border-l-2 border-primary pl-3 pb-2"
                        >
                          <div className="flex items-center justify-between">
                            <h3 className="font-medium">{event.title}</h3>
                            <Badge variant="outline">{event.hours} CEUs</Badge>
                          </div>
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {new Date(event.start_date).toLocaleDateString('en-US', {
                              weekday: 'long',
                              month: 'long',
                              day: 'numeric',
                            })}
                            {event.start_time && event.end_time && (
                              <span className="ml-1">
                                {event.start_time.slice(0, 5)} - {event.end_time.slice(0, 5)}
                              </span>
                            )}
                          </p>
                          {event.venue_details && (
                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {event.venue_details}
                            </p>
                          )}
                        </div>
                      ))}
                      <div className="text-center pt-2">
                        <Button variant="link" size="sm" asChild>
                          <Link to="/admin/courses">View All Courses</Link>
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <p className="text-sm text-muted-foreground">
                        No upcoming events scheduled in the next 30 days
                      </p>
                      <Button variant="link" size="sm" className="mt-2" asChild>
                        <Link to="/admin/courses/new">Create New Course</Link>
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            <>
              <Card className="hover-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5" />
                    Learning Achievements
                  </CardTitle>
                  <CardDescription>
                    Your professional development progress
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <AchievementsContent />
                </CardContent>
              </Card>

              <Card className="hover-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Learning Schedule
                  </CardTitle>
                  <CardDescription>
                    Your upcoming registered events
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <LearningScheduleContent />
                </CardContent>
              </Card>

              <Card className="hover-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Award className="h-5 w-5" />
                    Recent Activity
                  </CardTitle>
                  <CardDescription>
                    Recently completed courses
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <RecentActivityContent />
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
